import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatAgo, formatDateTime } from '../sso-workspace/format'
import { Pill } from '../sso-workspace/pill'
import { serverMessage } from '../error/server-message'
import { ErrorReference } from '../error/error-reference'
import { useErrorLine } from '../error/use-error-line'
import { getActivity } from './scim-api'
import { eventByIdentityProvider, eventRefused, eventText, eventUserID, failureRequestText, failureUserID } from './scim-words'

// What the identity provider did through one link, and what failed (spec
// R22): content's GET /scim/connections/:id/activity -- the link's scim.*
// events and its tokens' api_key.* events (newest first, the last 90 days),
// and the tokens' 20 latest failed requests (time, method, route, status,
// code; never a body, so never a name or an address).
//
// personHref(userID): where an event about a person links (the
// organization's own member page); none for staff.
// While it is open and the browser tab is visible the panel reads again
// every REFRESH_MS; a hidden tab reads nothing, and coming back reads at
// once. A refresh swaps the lists in place (no "Loading" flash) and a
// refresh that fails keeps what is shown.
export const REFRESH_MS = 10000

const tabVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden'

export const ScimActivity = ({ providerID, organizationID, personHref }) => {
  const [activity, setActivity] = useState(undefined)
  const [error, failure, setError] = useErrorLine()
  const latest = useRef(0)

  useEffect(() => {
    let live = true
    const read = async quiet => {
      const call = ++latest.current
      if (!quiet) {
        setActivity(undefined)
        setError(null)
      }
      try {
        const answer = await getActivity(providerID, organizationID)
        if (!live || call !== latest.current) return
        setActivity({ events: (answer && answer.events) || [], failures: (answer && answer.failures) || [] })
        setError(null)
      } catch (err) {
        if (!live || call !== latest.current || quiet) return
        setActivity(null)
        setError(serverMessage(err, "The activity couldn't be loaded."), err)
      }
    }

    read(false)

    let timer = null
    const start = () => {
      if (timer === null && tabVisible()) timer = setInterval(() => read(true), REFRESH_MS)
    }
    const stop = () => {
      if (timer !== null) clearInterval(timer)
      timer = null
    }
    const onVisibility = () => {
      if (tabVisible()) {
        read(true)
        start()
      } else {
        stop()
      }
    }
    start()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      live = false
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerID, organizationID])

  if (activity === undefined) return <p className="sw-hint m-0">Loading activity…</p>
  if (activity === null) {
    return (
      <div role="alert">
        <p className="text-danger small m-0">{error}</p>
        <ErrorReference error={failure} compact />
      </div>
    )
  }

  return (
    <div className="d-grid gap-3" data-testid="scim-activity">
      <div>
        <div className="sw-subh mb-2"><h3>Recent activity</h3></div>
        {activity.events.length === 0
          ? <p className="sw-hint m-0">Nothing yet. The people and groups the identity provider adds, updates and removes show here, with every change to the token.</p>
          : <ul className="sw-notes">
            {activity.events.map((event, i) => {
              const userID = eventUserID(event)
              return (
                <li key={`${event.at}-${i}`} data-tone={eventRefused(event) || event.action === 'scim.user.deactivated' || event.action === 'scim.user.deleted' || event.action === 'scim.group.deleted' || event.action === 'api_key.revoked' ? 'warn' : undefined}>
                  <span>
                    <span className="fw-semibold">{eventText(event)}</span>
                    <span className="sw-hint" title={formatDateTime(event.at)}> · {formatAgo(event.at)}</span>
                    <span className="sw-hint"> · {eventByIdentityProvider(event) ? 'by the identity provider' : 'token'}</span>
                    {userID && personHref && <> · <Link to={personHref(userID)}>View person</Link></>}
                  </span>
                </li>
              )
            })}
          </ul>}
      </div>

      <div>
        <div className="sw-subh mb-2"><h3>Latest failed requests</h3></div>
        {activity.failures.length === 0
          ? <p className="sw-hint m-0">No failed requests. Requests the identity provider sent that EducationXR refused show here.</p>
          : <div className="table-responsive">
            <table className="sw-table" aria-label="latest failed requests">
              <thead>
                <tr><th scope="col">When</th><th scope="col">Request</th><th scope="col">Status</th><th scope="col">Code and reason</th></tr>
              </thead>
              <tbody>
                {activity.failures.map((f, i) =>
                  <tr key={`${f.at}-${i}`}>
                    <td title={formatDateTime(f.at)}>{formatAgo(f.at)}</td>
                    <td className="sw-mono small">{failureRequestText(f)}</td>
                    <td><Pill tone={f.status >= 500 ? 'bad' : 'warn'}>{f.status}</Pill></td>
                    <td>
                      <span className="sw-mono small">{f.code || <span className="sw-hint">None</span>}</span>
                      {f.detail && <div className="small" data-testid="failure-reason">{f.detail}</div>}
                      {failureUserID(f) && personHref && <div className="small"><Link to={personHref(failureUserID(f))}>View person</Link></div>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
    </div>
  )
}
